import { useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { applicationApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/Feedback'
import { Modal } from '@/components/ui/Modal'
import { PageHeader } from '@/components/ui/Page'
import { Sheet } from '@/components/ui/Sheet'
import { ApplicationStatusPill } from '@/components/ui/StatusPill'
import { Segmented } from '@/components/ui/Toggle'
import { InboxView } from '@/features/mail/InboxView'
import { useMailStats } from '@/features/mail/mailMeta'
import { cn, formatDate, humanise, relativeTime } from '@/lib/utils'
import { isLicenseError } from '@/store/license'
import { toast } from '@/store/toast'
import { refreshLicense, useLicense } from '@/features/billing/useLicense'
import type { ApplicationDetail, ReviewApplication } from '@/types/api'

import { ApplicationStatTiles, ApplicationsTable, StatusTabs, statusesFor } from './ApplicationsTable'
import { PipelineView } from './PipelineView'
import { fromCurrent } from '@/features/applications/backTarget'

type TrackerTab = 'inbox' | 'pipeline' | 'applications'

/** The status filters the list understands; anything else in `?status=` is ignored. */
const STATUS_FILTERS = new Set(['all', 'submitted', 'in_flight', 'needs_you', 'failed', 'skipped'])

export function TrackerPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [status, setStatus] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  useEffect(() => setPage(1), [status, search])
  const [detail, setDetail] = useState<ApplicationDetail | null>(null)
  const navigate = useNavigate()

  // The active tab lives in `?tab=`; a deep link to a message implies the inbox.
  const rawTab = searchParams.get('tab')
  const tab: TrackerTab =
    rawTab === 'pipeline' || rawTab === 'applications' || rawTab === 'inbox'
      ? rawTab
      : searchParams.get('application')
        ? 'applications'
        : searchParams.get('message') ? 'inbox' : 'applications'
  const setTab = (next: TrackerTab) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        params.set('tab', next)
        if (next !== 'inbox') params.delete('message')
        return params
      },
      { replace: true },
    )
  const mailStats = useMailStats()
  const unreadMail = mailStats.data?.unread ?? 0

  // Deep link: ?status=needs_you (the command palette sends people here) preselects
  // that filter, then leaves the URL so the filter they pick next is the truth.
  const requestedStatus = searchParams.get('status')
  useEffect(() => {
    if (!requestedStatus) return
    if (STATUS_FILTERS.has(requestedStatus)) {
      setStatus(requestedStatus)
    }
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        params.delete('status')
        if (STATUS_FILTERS.has(requestedStatus)) params.set('tab', 'applications')
        return params
      },
      { replace: true },
    )
  }, [requestedStatus, setSearchParams])

  const reliability = useQuery({ queryKey: ['application-reliability'], queryFn: applicationApi.reliability, refetchInterval: 30_000, retry: false })

  const statsQuery = useQuery({
    queryKey: ['application-stats'],
    queryFn: applicationApi.stats,
    staleTime: 20_000,
    refetchInterval: 15_000,
  })

  const listQuery = useQuery({
    queryKey: ['applications', status, search, page],
    queryFn: () =>
      applicationApi.list({
        page,
        pageSize: 50,
        statuses: statusesFor(status),
        search: search.trim() || undefined,
      }),
    staleTime: 15_000,
    refetchInterval: 15_000,
    enabled: tab === 'applications',
  })

  // Deep link: /tracker?application=<id> opens that row directly.
  const requestedId = searchParams.get('application')
  useEffect(() => {
    if (!requestedId) return
    applicationApi
      .get(requestedId)
      .then(setDetail)
      .catch(() => toast.error('That application could not be found'))
      .finally(() => {
        setSearchParams(
          (current) => {
            const params = new URLSearchParams(current)
            params.delete('application')
            return params
          },
          { replace: true },
        )
      })
  }, [requestedId, searchParams, setSearchParams])

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['applications'] })
    queryClient.invalidateQueries({ queryKey: ['application-stats'] })
    queryClient.invalidateQueries({ queryKey: ['application-reliability'] })
    queryClient.invalidateQueries({ queryKey: ['review-inbox'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] })
    refreshLicense(queryClient)
  }
  // Retry / submit / run again all send applications: no plan → the upgrade dialog instead.
  const { requireApply } = useLicense()

  const retry = useMutation({
    mutationFn: (id: string) => applicationApi.retry(id),
    onSuccess: (application) => {
      refresh()
      setDetail(application)
      toast.success('Retrying', application.job.companyName)
    },
    onError: (error: ApiError) => {
      if (!isLicenseError(error)) toast.error('Could not retry', error.message)
    },
  })

  const cancel = useMutation({
    mutationFn: (id: string) => applicationApi.cancel(id),
    onSuccess: () => {
      refresh()
      setDetail(null)
      toast.info('Application cancelled')
    },
    onError: (error: ApiError) => toast.error('Could not cancel', error.message),
  })

  // Waiting applications whose details are all filled (the profile or an
  // answer elsewhere filled them): one click sends them all.
  const reviewQuery = useQuery({
    queryKey: ['review-inbox'],
    queryFn: applicationApi.reviewInbox,
    refetchInterval: 20_000,
  })
  const ready = (reviewQuery.data?.applications ?? []).filter((a) => a.waitingFor === 'ready')
  const readyCount = ready.length
  const [confirmReady, setConfirmReady] = useState(false)
  const submitReady = useMutation({
    mutationFn: () => applicationApi.submitReady(),
    onSuccess: ({ queued }) => {
      refresh()
      setConfirmReady(false)
      if (queued.length) {
        toast.success(`${queued.length} application${queued.length === 1 ? '' : 's'} queued`, 'Processed one at a time. Follow progress in Applications.')
      } else {
        toast.info('Nothing was ready to submit')
      }
    },
    onError: (error: ApiError) => {
      setConfirmReady(false)
      if (!isLicenseError(error)) toast.error('Could not submit', error.message)
    },
  })

  // Applications a CAPTCHA stopped (nothing was sent): run them again and
  // solve each on its live page as the banner announces it.
  const captchaStopped = useQuery({
    queryKey: ['applications-captcha-stopped'],
    queryFn: applicationApi.captchaStopped,
    staleTime: 30_000,
    retry: false,
  })
  const retryCaptcha = useMutation({
    mutationFn: () => applicationApi.retryCaptchaStopped(),
    onSuccess: ({ queued }) => {
      refresh()
      queryClient.invalidateQueries({ queryKey: ['applications-captcha-stopped'] })
      if (queued.length) {
        toast.success(
          `Running ${queued.length} application${queued.length === 1 ? '' : 's'} again`,
          'Keep this tab open: each one asks for you when it reaches its CAPTCHA.',
        )
      } else {
        toast.info('Nothing to run again')
      }
    },
    onError: (error: ApiError) => {
      if (!isLicenseError(error)) toast.error('Could not retry', error.message)
    },
  })
  const stoppedCount = captchaStopped.data?.count ?? 0

  const open = (id: string) => applicationApi.get(id).then(setDetail)
  const unfiltered = status === 'all' && !search.trim()

  return (
    <div className="space-y-4 sm:space-y-5">
      <PageHeader eyebrow="Your job search" title="Application tracking" description="Follow application progress, respond to requests, and read employer emails." actions={<Button variant="secondary" size="sm" onClick={refresh}>Refresh</Button>} />
      <div className="rounded-card border border-line bg-surface px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-[15px] font-semibold text-ink">Application queue</h2>
            <p className="mt-1 text-[13px] text-ink-muted">{statsQuery.data?.queued ?? 0} queued · {statsQuery.data?.inFlight ?? 0} in progress · {statsQuery.data?.submitted ?? 0} submitted</p>
          </div>
          <span className="rounded-pill bg-brand-soft px-3 py-1 text-[12px] font-medium text-brand">One application at a time</span>
        </div>
      </div>

      {reliability.data && typeof reliability.data.submittedToday === 'number' && (
        <section className="card p-4 sm:p-5" aria-label="Submission results">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><h2 className="text-[15px] font-semibold text-ink">Submission results</h2><p className="mt-1 text-[13px] text-ink-muted">{reliability.data.submittedToday} submitted today · {reliability.data.submitted} submitted from the last {reliability.data.cohortDays} days of applications</p></div>
            <span className="text-[15px] font-semibold text-brand">{reliability.data.submissionRate === null ? 'No settled attempts yet' : `${reliability.data.submissionRate}% submission rate`}</span>
          </div>
          <p className="mt-2 text-[12px] text-ink-muted">Submitted ÷ (submitted + failed + awaiting your review). Active runs and test runs are excluded from the rate.</p>
          <div className="mt-3 flex flex-wrap gap-2">{reliability.data.byPortal.map((p) => <span key={p.portal} className="rounded-pill border border-line px-2.5 py-1 text-[12px] text-ink-soft">{p.portal}: {p.submitted} submitted{p.submissionRate !== null ? ` · ${p.submissionRate}%` : ''}</span>)}</div>
        </section>
      )}

      {(statsQuery.data?.needsYou ?? 0) > 0 && (
        <ReviewInboxBanner count={statsQuery.data!.needsYou} onOpen={() => navigate('/dashboard/review')} />
      )}

      {stoppedCount > 0 && (
        <CaptchaStoppedBanner count={stoppedCount} pending={retryCaptcha.isPending} onRetry={() => requireApply(() => retryCaptcha.mutate())} />
      )}

      <ViewSwitch tab={tab} unread={unreadMail} onChange={setTab} />

      {tab === 'inbox' && <InboxView />}
      {tab === 'pipeline' && <PipelineView />}

      {tab === 'applications' && (
        <>
          <ApplicationStatTiles stats={statsQuery.data} active={status} onChange={setStatus} />

          <section className="card overflow-hidden">
            <header className="flex flex-col gap-3 border-b border-line px-3 py-3 sm:px-4 md:flex-row-reverse md:items-center md:justify-between">
              <div className="flex items-center gap-2 md:shrink-0">
                <SearchField value={search} onChange={setSearch} />
                {readyCount > 0 && (
                  <Button size="sm" onClick={() => requireApply(() => setConfirmReady(true))} className="hidden shrink-0 md:inline-flex">
                    <SubmitReadyLabel count={readyCount} />
                  </Button>
                )}
              </div>
              <div className="min-w-0 md:flex-1">
                <StatusTabs active={status} stats={statsQuery.data} onChange={setStatus} />
              </div>
            </header>

            {listQuery.isError ? (
              <ErrorState onRetry={() => listQuery.refetch()} />
            ) : (
              <ApplicationsTable
                applications={listQuery.data?.data ?? []}
                loading={listQuery.isLoading}
                onOpen={(application) => open(application.id)}
                emptyTitle={unfiltered ? 'No applications yet' : 'Nothing matches this view'}
                emptyDescription={
                  unfiltered
                    ? 'Apply to a job from your matches and track every step of it here.'
                    : 'Try another status or clear the search.'
                }
                emptyAction={
                  unfiltered ? (
                    <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard/jobs')}>
                      Browse jobs
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSearch('')
                        setStatus('all')
                      }}
                    >
                      Show all applications
                    </Button>
                  )
                }
              />
            )}
          </section>

          {listQuery.data?.pageInfo && (
            <div className="flex flex-wrap items-center justify-between gap-3 text-[13px] text-ink-muted">
              <span>{listQuery.data.pageInfo.totalItems} applications · Page {page} of {Math.max(1, listQuery.data.pageInfo.totalPages)}</span>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={!listQuery.data.pageInfo.hasPrevious || listQuery.isFetching} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button variant="secondary" size="sm" disabled={!listQuery.data.pageInfo.hasNext || listQuery.isFetching} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}

          {/* Phones: the primary action rides above the tab bar while the list scrolls. */}
          {readyCount > 0 && (
            <div className="action-bar md:hidden">
              <Button fullWidth onClick={() => requireApply(() => setConfirmReady(true))}>
                <SubmitReadyLabel count={readyCount} />
              </Button>
            </div>
          )}
        </>
      )}

      <Modal
        open={confirmReady}
        onClose={() => setConfirmReady(false)}
        title={`Submit ${readyCount} ready application${readyCount === 1 ? '' : 's'}?`}
        description="All ready applications will join your queue, including those outside the current page or filter. They run one at a time; progress continues after you leave this screen."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmReady(false)}>
              Not yet
            </Button>
            <Button
              onClick={() => {
                if (!requireApply(() => submitReady.mutate())) setConfirmReady(false)
              }}
              loading={submitReady.isPending}
            >
              Queue all ready
            </Button>
          </>
        }
      >
        <ReadyList applications={ready} />
      </Modal>

      <Sheet
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail?.job.title}
        description={detail ? `${detail.job.companyName}${detail.job.location ? ` · ${detail.job.location}` : ''}` : undefined}
        size="lg"
        footer={
          detail && (
            <>
              {(detail.status === 'failed' || detail.needsReview) && (
                <Button variant="secondary" size="sm" onClick={() => {
                    if (!requireApply(() => retry.mutate(detail.id))) setDetail(null)
                  }}
                  loading={retry.isPending}
                >
                  Retry
                </Button>
              )}
              {detail.status === 'needs_you' && !detail.needsReview && (
                <Button size="sm" onClick={() => navigate(`/dashboard/applications/${detail.id}`, { state: fromCurrent() })}>
                  {detail.readyToSubmit ? 'Review and submit' : 'Answer questions'}
                </Button>
              )}
              {detail.status !== 'submitted' && (
                <Button variant="ghost" size="sm" onClick={() => cancel.mutate(detail.id)} loading={cancel.isPending}>
                  Cancel
                </Button>
              )}
              <a
                href={detail.job.applyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 items-center rounded-field border border-line px-3.5 text-[13px] font-medium text-ink transition-colors hover:bg-surface-muted"
              >
                View posting
              </a>
            </>
          )
        }
      >
        {detail && <DetailBody detail={detail} />}
      </Sheet>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------
const VIEWS: Array<{ value: TrackerTab; label: string; shortLabel?: string }> = [
  { value: 'applications', label: 'Applications' },
  { value: 'pipeline', label: 'Hiring pipeline', shortLabel: 'Pipeline' },
  { value: 'inbox', label: 'Employer inbox', shortLabel: 'Inbox' },
]

/** Inbox / Pipeline / Applications. Sticks under the app header on phones so it is always a thumb away. */
function ViewSwitch({ tab, unread, onChange }: { tab: TrackerTab; unread: number; onChange: (tab: TrackerTab) => void }) {
  const options = VIEWS.map((view) => (view.value === 'inbox' && unread > 0 ? { ...view, count: unread } : view))
  return (
    <div
      className="sticky z-20 -mx-4 bg-canvas/95 px-4 py-2 backdrop-blur-md md:static md:mx-0 md:bg-transparent md:px-0 md:py-0 md:backdrop-blur-none"
      style={{ top: 'calc(56px + var(--safe-top))' }}
    >
      <Segmented options={options} value={tab} onChange={onChange} aria-label="Tracker views" fullWidth className="md:w-auto md:inline-flex" />
    </div>
  )
}

/** Every waiting application's questions in one place: answer once, submit all. */
function ReviewInboxBanner({ count, onOpen }: { count: number; onOpen: () => void }) {
  return (
    <div role="status" className="flex flex-col gap-3 rounded-card border border-brand/30 bg-brand-soft/60 px-4 py-3 sm:flex-row sm:items-center">
      <p className="min-w-0 flex-1 text-[13px] leading-snug text-ink">
        <span className="font-semibold">
          <span className="num">{count}</span> application{count === 1 ? ' needs' : 's need'} you.
        </span>{' '}
        Open Needs review to answer missing questions and queue every eligible application.
      </p>
      <Button size="sm" onClick={onOpen} className="shrink-0">
        Open Needs review
      </Button>
    </div>
  )
}

function CaptchaStoppedBanner({ count, pending, onRetry }: { count: number; pending: boolean; onRetry: () => void }) {
  return (
    <div
      role="status"
      className="flex flex-col gap-3 rounded-card border border-warning/30 bg-warning/[0.06] px-4 py-3 sm:flex-row sm:items-center"
    >
      <p className="flex min-w-0 flex-1 items-start gap-2.5 text-[13px] leading-snug text-ink">
        <span className="relative mt-[5px] flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-warning" />
        </span>
        <span>
          <span className="font-semibold">
            <span className="num">{count}</span> application{count === 1 ? ' was' : 's were'} stopped by a CAPTCHA.
          </span>{' '}
          Nothing was sent. Run them again and solve each one live when it asks.
        </span>
      </p>
      <Button
        variant="secondary"
        onClick={onRetry}
        loading={pending}
        className="w-full shrink-0 sm:h-9 sm:w-auto sm:px-3.5 sm:text-[13px]"
      >
        Run them again
      </Button>
    </div>
  )
}

function SearchField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="relative min-w-0 flex-1 md:w-[260px] md:flex-none">
      <svg
        className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="6" cy="6" r="4.2" stroke="currentColor" strokeWidth="1.5" />
        <path d="m9.3 9.3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search company or role…"
        aria-label="Search applications"
        className="field-box h-10 w-full py-1.5 pl-8 text-[14px] md:h-9"
      />
    </div>
  )
}

/** One child, so the button's flex gap does not split the brackets from the number. */
function SubmitReadyLabel({ count }: { count: number }) {
  return (
    <span>
      Queue all ready (<span className="num">{count}</span>)
    </span>
  )
}

function ReadyList({ applications }: { applications: ReviewApplication[] }) {
  return (
    <ul className="max-h-80 overflow-y-auto divide-y divide-line rounded-card border border-line">
      {applications.map((a) => (
        <li key={a.id} className="px-4 py-2.5">
          <p className="truncate text-[14px] font-medium text-ink">{a.company}</p>
          <p className="truncate text-[13px] text-ink-muted">{a.jobTitle}</p>
        </li>
      ))}
    </ul>
  )
}

function DetailBody({ detail }: { detail: ApplicationDetail }) {
  return (
    <div className="space-y-6 pb-2">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-4">
        <Fact label="Status">
          <ApplicationStatusPill status={detail.status} readyToSubmit={detail.readyToSubmit} needsReview={detail.needsReview} />
        </Fact>
        <Fact label="Match">
          <span className="num">{detail.matchScore != null ? `${detail.matchScore}%` : '—'}</span>
        </Fact>
        <Fact label="Resume">{detail.resumeVariant === 'optimized' ? 'Tailored' : humanise(detail.resumeVariant)}</Fact>
        <Fact label="Applied">
          <span className="num">{detail.submittedAt ? formatDate(detail.submittedAt) : 'Not yet'}</span>
        </Fact>
      </dl>

      {detail.failureReason && (
        <p className="rounded-field bg-danger/[0.08] px-3.5 py-2.5 text-[14px] leading-relaxed text-danger">{detail.failureReason}</p>
      )}

      {detail.screeningQuestions.length > 0 && (
        <section>
          <h3 className="label-eyebrow mb-3">Screening answers</h3>
          <ul className="divide-y divide-line rounded-card border border-line">
            {detail.screeningQuestions.map((question) => (
              <li key={question.questionKey} className="px-4 py-3">
                <p className="text-[13.5px] text-ink-soft">{question.questionText}</p>
                <p className="mt-1 flex flex-wrap items-center gap-2 text-[14.5px] font-medium text-ink">
                  {question.answerValue || <span className="text-ink-faint">Not answered</span>}
                  {question.wasAutofilled && question.answerValue && (
                    <span className="rounded-pill bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-ink-soft">Autofilled</span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h3 className="label-eyebrow mb-3">Timeline</h3>
        {detail.events.length === 0 ? (
          <p className="text-[13px] text-ink-muted">Nothing has happened yet.</p>
        ) : (
          <ol className="space-y-3">
            {detail.events.map((event, index) => (
              <li key={event.id} className="flex gap-3">
                <span className={cn('mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full', index === 0 ? 'bg-ink' : 'bg-line-strong')} />
                <div className="min-w-0">
                  <p className="text-[14px] leading-snug text-ink">{event.message ?? humanise(event.eventType)}</p>
                  <p className="num mt-0.5 text-[12px] text-ink-muted">{relativeTime(event.createdAt)}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="label-eyebrow">{label}</dt>
      <dd className="mt-1.5 truncate text-[14.5px] text-ink">{children}</dd>
    </div>
  )
}
